import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { DateParserService } from 'libraries/service/date/dateParser.service';
import { IDateParserOptions } from 'libraries/service/date/IDateParserOptions';
import { LanguageService } from "libraries/service/language/language.service";
import { Calendar } from "primeng/calendar";
import { timer } from "rxjs";
import { IDateFieldSpecs } from '../interfaces/field-specs';
import { ILocale, LOCALE } from './config/locale';
import { EDateInputAdditionalClass } from "./date-additional-class.enum";

@Component({
    selector: 'date-input',
    template: `
        <div class="field"
             [ngClass]="additionnalClass"
             [class.full]="isFull"
             [class.disabled]="fieldConfig.disabled"
             [formGroup]="fieldConfig.formGroup">
            <fa-icon [icon]="iconCalendar || defaultIconCalendar"
                     class="icon"
                     (click)="iconClick()"></fa-icon>
            <p-calendar #calendar
                        [readonlyInput]="true"
                        class="date-field-desktop field date-field e2e-calendar-field"
                        [disabled]="!!fieldConfig.disabled"
                        [class.only-one-month]="onlyOneMonth"
                        [class.full]="isFull"
                        [dateFormat]="(LOCALE[languageService.userLanguage] || LOCALE.fr).dateFormat"
                        [locale]="LOCALE[languageService.userLanguage] || LOCALE.fr"
                        [numberOfMonths]="onlyOneMonth ? 1 : 2"
                        [formControlName]="fieldConfig.formControlName"
                        [yearRange]="yearRange"
                        [yearNavigator]="yearNavigator"
                        [monthNavigator]="monthNavigator"
                        [placeholder]="fieldConfig.placeholder"
                        [tabindex]="fieldConfig.tabindex"
                        [selectOtherMonths]="true"
                        [appendTo]="fieldConfig.appendTo"
                        [minDate]="fieldConfig.minDate"
                        [maxDate]="fieldConfig.maxDate"
                        [defaultDate]="fieldConfig.defaultDate"
                        [hideOnDateTimeSelect]="closeDatePickerAfterSelection"
                        [readonlyInput]="!!fieldConfig.disabled"
                        (onBlur)="convertDate($event)"
                        (onFocus)="onFocus.emit($event)"
                        (onSelect)="onSelect.emit($event)"
                        (onInput)="validateAllFormFields(fieldConfig.formGroup)">
            </p-calendar>
            <p-calendar #calendar
                        class="date-field-mobile field date-field e2e-calendar-field"
                        [readonlyInput]="true"
                        [disabled]="!!fieldConfig.disabled"
                        [class.only-one-month]="onlyOneMonth"
                        [class.full]="isFull"
                        [dateFormat]="(LOCALE[languageService.userLanguage] || LOCALE.fr).dateFormat"
                        [locale]="LOCALE[languageService.userLanguage] || LOCALE.fr"
                        [numberOfMonths]="onlyOneMonth ? 1 : 2"
                        [formControlName]="fieldConfig.formControlName"
                        [yearRange]="yearRange"
                        [yearNavigator]="yearNavigator"
                        [monthNavigator]="monthNavigator"
                        [placeholder]="fieldConfig.placeholder"
                        [tabindex]="fieldConfig.tabindex"
                        [selectOtherMonths]="true"
                        [appendTo]="fieldConfig.appendTo"
                        [minDate]="fieldConfig.minDate"
                        [maxDate]="fieldConfig.maxDate"
                        [defaultDate]="fieldConfig.defaultDate"
                        [hideOnDateTimeSelect]="closeDatePickerAfterSelection"
                        [readonlyInput]="!!fieldConfig.disabled"
                        [touchUI]="true"
                        (onBlur)="convertDate($event)"
                        (onFocus)="onFocus.emit($event)"
                        (onSelect)="onSelect.emit($event)"
                        (onInput)="validateAllFormFields(fieldConfig.formGroup)">
                <div class="calendar-2-month-before-input-content">
                    <ng-content select=".date-input-before-input-content"></ng-content>
                </div>
            </p-calendar>
        </div>
    `,
    styleUrls: ['./date-input.component.scss']
})

export class DateInputComponent implements OnChanges {
    @Input()
    public fieldConfig: IDateFieldSpecs;
    @Input()
    public yearNavigator: boolean;
    @Input()
    public monthNavigator: boolean;
    @Input()
    public alignRightDatePicker: boolean = false;
    @Input()
    public onlyOneMonth: boolean;
    @Input()
    public isFull: boolean = false;
    @Input()
    public name: string;
    @Input()
    public isDateReturnedOnBlur: boolean = false;
    @Input()
    public closeDatePickerAfterSelection: boolean = true;
    @Input()
    public isPositionFixed: boolean = false;
    @Input()
    public additionnalClass: EDateInputAdditionalClass;
    @Input()
    public baseZIndex: number;
    @Input()
    public iconCalendar: IconDefinition = faCalendar;

    public defaultIconCalendar: IconDefinition = faCalendar;

    @Output()
    public readonly onBlur: EventEmitter<boolean> = new EventEmitter();

    @Output()
    public readonly onSelect: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly selectDate: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly onFocus: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('calendar')
    public readonly calendar: Calendar;

    public readonly LOCALE: ILocale = LOCALE;

    public yearRange: string;
    public disabled: boolean = false;
    public value: string;

    constructor(private readonly dateParser: DateParserService,
        public readonly languageService: LanguageService,) {
    }

    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges && this.fieldConfig) {
            this.validateAllFormFields(this.fieldConfig.formGroup);

            //init fieldConfigyearRange if needed and not provided
            if (this.yearNavigator &&
                this.fieldConfig.minDate &&
                this.fieldConfig.maxDate &&
                !this.fieldConfig.yearRange) {
                this.fieldConfig.yearRange = {
                    min: this.fieldConfig.minDate.getFullYear(),
                    max: this.fieldConfig.maxDate.getFullYear()
                };
            }

            if (this.fieldConfig.yearRange &&
                this.fieldConfig.yearRange.min &&
                this.fieldConfig.yearRange.max) {
                this.yearRange =
                    `${this.fieldConfig.yearRange.min.toString()}:${this.fieldConfig.yearRange.max.toString()}`;
            }

            if (this.fieldConfig.disabled) {
                this.fieldConfig.formGroup.disable({emitEvent: false});
            } else {
                this.fieldConfig.formGroup.enable({emitEvent: false});
            }

            //avoid strange behaviour when minDate is sup at typed date
            if(this.fieldConfig.minDate && this.fieldConfig.formGroup.get(this.fieldConfig.formControlName)){
                const fieldDate: Date = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
                const minDate: Date = this.fieldConfig.minDate;
                if(fieldDate && minDate && fieldDate < minDate){
                    this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).patchValue(null);
                }

            }
        }
    }

    /**
     * Convert Date 25Oct to 25/10/20**
     */
    public convertDate(event: FocusEvent): void {
        const field: AbstractControl = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName);
        const value: string = event.target['value'] || event.srcElement['value'];

        const boundaries: IDateParserOptions = {
            min: this.fieldConfig.minDate,
            max: this.fieldConfig.maxDate,
        };
        if (this.fieldConfig.forceYearInPast){
            boundaries.max = boundaries.max || new Date();
            boundaries.yearsIncrement = 100;
        }
        const parsed: Date = this.dateParser.parse(value, boundaries);

        // this comparison is required to allow date update to work

        if ((field.value && typeof field.value?.toISOString !== 'undefined') && parsed && parsed.toISOString() !== field.value?.toISOString()) {
            field.patchValue(parsed);
        }else if(!field.value && parsed){
            field.patchValue(parsed);
        }

        this.onBlur.emit(this.isDateReturnedOnBlur ? field.value : event);
    }

    public validateAllFormFields(formGroup: FormGroup): void {
        Object.keys(formGroup.controls)
            .forEach((field: string) => {
                const formField: AbstractControl = formGroup.get(field);
                formField.updateValueAndValidity();
            });
    }

    public asyncToggle(): void {
        timer().subscribe((): void => {
            // async call required
            this.calendar.toggle();
        });
    }

    public iconClick(): void {
        this.calendar.toggle();
        this.calendar.showOverlay();
    }
}
